import { onAtlasReset } from './atlas.ts';
/* EVERY CREATURE HAS BONES (LS-185).
 *
 * The forager got a rig (LS-184); this gives one to everything else. Each
 * kind has a BODY PLAN -- a pose function from its state (walk phase, wind,
 * charge, stun, flipped, reel, alight, spent, enraged) to an ordered list of
 * primitives in its own palette -- painted by the same engine as the
 * player's: capsules and discs snapped to whole pixels, a one-pixel outline,
 * cached per quantised pose. The hit flash paints the whole figure paper.
 *
 *   sporeling   a mushroom sprite: cap, stalk, two stubby legs; it hops
 *   bramble     a thorn beast: a bristling body on four scuttling legs;
 *               alight, the thorns are flames
 *   wisp        no bones: a core, a halo, three trailing tails
 *   shellback   a domed shell with plates, a head that pokes out to walk and
 *               pulls in when hit; on its back the legs wave
 *   charger     a boar: rump, shoulders, a low head with tusks, four legs
 *               that gallop when it charges and paw when it winds up
 *   warden      the stone guardian: a great torso, a horned head with lit
 *               eyes, two arms that rise to wind and hang when spent
 *   brood       the Broodmother: a pulsing sac on six splayed legs
 *
 * Everything is a pure function of the state, so the same creature in the
 * same state paints the same pixels. */
import * as M3 from './model3d.ts';
import { PALETTE as P, ALT, UI, wetTone } from './art.ts';
import { grid, cap, disc, put, outline, bakeGrid, ik2, type XY, type Grid } from './rig.ts';

export type CreatureState = {
  type: string;
  dir: string;   // one of eight (LS-194): 'up', 'up-right', 'right', ...
  anim: number;
  moving: boolean;
  state: string;          // 'idle' | 'wind' | 'charge' | 'spent'
  stun: number;
  flipped: number;
  reel: number;
  alight: number;
  launched: boolean;
  enraged: boolean;
  turned: number;
  alt: string | null;
  white: boolean;
  pattern?: number;
  landed?: number;        // frames since it came down from a throw: a squash
  cool?: number;          // frames until a shooter fires: the last thirty are the telegraph (the art lens, 2026-09-05: the small kinds' states were identical)
  wet?: boolean;          // soaked, or standing in the rain: painted in its wet state (2026-09-11)
};

/* The cell for each kind, centred on the body's centre. */
export const CELL: Record<string, number> = { sporeling: 24, bramble: 24, wisp: 24, shellback: 26, charger: 28, sow: 64, oldshell: 64, bramblemother: 64, gloam: 24, cinder: 24, eel: 26, tidecrab: 26, mossback: 24, sunbasker: 26, screehopper: 24, stormgull: 28, duskling: 24 };

const q = (v: number) => Math.round(v * 2) / 2;

type Op = ['cap', XY, XY, number, string] | ['disc', number, number, number, number, string] | ['px', number, number, string];

function paint(ops: Op[], w: number, h: number, ink: string): Grid {
  const gr = grid(w, h);
  for (const op of ops) {
    if (op[0] === 'cap') cap(gr, op[1], op[2], op[3], op[4]);
    else if (op[0] === 'disc') disc(gr, op[1], op[2], op[3], op[4], op[5]);
    else put(gr, Math.round(op[1]), Math.round(op[2]), op[3]);
  }
  rimLight(gr);
  outline(gr, ink, true);   // the body, not its shadow
  return gr;
}

/* A RIM LIGHT ON THE SUN SIDE (2026-09-05, against the reference renders). The sun in this world is up and to the
 * left, and a body lit by it has a bright lip along its upper-left edge. Every opaque pixel whose neighbour up or
 * left is empty takes a lighter shade of its own colour, so a creature stands off the ground it is on without any
 * plan being rewritten. The shadow discs, which are translucent, are left alone. */
function rimLight(gr: Grid) {
  const out = gr.px.slice();
  for (let y = 0; y < gr.h; y++) for (let x = 0; x < gr.w; x++) {
    const i = y * gr.w + x, c = gr.px[i];
    if (!c || c.length > 7) continue;
    const up = y > 0 ? gr.px[i - gr.w] : null, left = x > 0 ? gr.px[i - 1] : null;
    const openUp = !up || up.length > 7, openLeft = !left || left.length > 7;
    if (!openUp && !openLeft) continue;
    out[i] = M3.lighten(c, openUp && openLeft ? 0.34 : 0.2);
  }
  gr.px = out;
}

const keyOf = (type: string, ops: Op[], white: boolean, wet = false) =>
  type + (white ? 'W' : '') + (wet ? 'w' : '') + '|' + ops.map((op) => op.map((v) => Array.isArray(v) ? v.map(q).join(':') : typeof v === 'number' ? q(v) : v).join(',')).join(';');

/* EIGHT FACINGS (LS-194). Every body plan is written in terms of these two
 * numbers, so the four diagonals cost nothing but reading the name: a
 * diagonal is 0.72 of each axis (a unit vector at 45 degrees), which keeps a
 * charger's body the same length whichever way it points. */
const facingX = (dir: string) => dir.endsWith('left') ? (dir.indexOf('-') > 0 ? -0.72 : -1) : dir.endsWith('right') ? (dir.indexOf('-') > 0 ? 0.72 : 1) : 0;
const facingY = (dir: string) => dir.startsWith('up') ? (dir.indexOf('-') > 0 ? -0.72 : -1) : dir.startsWith('down') ? (dir.indexOf('-') > 0 ? 0.72 : 1) : 0;

/* --------------------------------------------------------------- plans --- */

/* EVERY BODY IS BUILT IN THREE DIMENSIONS NOW (LS-199).
 *
 * Vijay: "this is fantastic i want to do this for every asset", and "let's
 * use the 3d pipeline to make all assets consistent perspective".
 *
 * The plans used to place parts in SCREEN space and turn them by hand with a
 * pair of facing numbers, which is how a boar came to be drawn head-on when
 * it ran south-east and why nothing in the picture quite agreed about where
 * the ground was. A `stage` builds a body in CREATURE space instead -- x the
 * body's right, y up from the ground, z the way it faces -- rotates it by the
 * heading and projects it through the one camera every asset now shares
 * (`model3d.ts`: height full, depth at half). The parts come out of `ops()`
 * sorted back to front, so a leg on the far side is behind the body without
 * anybody writing an order down.
 *
 * The primitives are still a capsule, an ellipse and a pixel, painted by the
 * same engine into the same palette with the same one-pixel outline: the
 * geometry moved into three dimensions, the LOOK did not move at all. */
import { angleOf, ik3, type XYZ } from './rig3d.ts';
import { HK, DK } from './model3d.ts';

/* The body's lateral axis takes 45% of its depth, exactly as the forager's
 * does: it keeps a creature seen from the side standing on one line rather
 * than leaning up the screen, while everything the animation moves along the
 * FORWARD axis foreshortens honestly. */
const LAT = 0.45;

type Part = { d: number; op: Op };

function stage(dir: string, C: number, F: number) {
  const a = angleOf(dir);
  const cosA = Math.cos(a), sinA = Math.sin(a);
  const parts: Part[] = [];
  const world = (x: number, y: number, z: number): XYZ => [-sinA * x + cosA * z, y, cosA * x * LAT + sinA * z];
  const flat = (p: XYZ): XY => [C + p[0], F - p[1] * HK + p[2] * DK];
  const depth = (p: XYZ) => p[2] + p[1] * 0.08;
  const api = {
    a, cosA, sinA,
    /** creature space -> the screen */
    at: (x: number, y: number, z: number): XY => flat(world(x, y, z)),
    world,
    /** A solid: an ellipsoid `r` wide, `rz` deep and `ry` tall. All three
     *  matter -- a turtle's shell is a wide shallow dome and a warden's torso
     *  is a boulder, and with one radius they came out as the same sphere. */
    ball(x: number, y: number, z: number, r: number, colour: string, rz = r, ry = r) {
      const w = world(x, y, z), f = flat(w);
      { const cap3 = (globalThis as any).__m3Capture; if (cap3) cap3.push({ kind: 'ball', w, r, rz, ry, colour }); }   // the solids themselves, for a probe (2026-09-13)
      parts.push({ d: depth(w), op: ['disc', f[0], f[1], r, Math.max(0.6, ry * HK * 0.62 + rz * DK * 0.9), colour] });
      return f;
    },
    /** a flat disc lying ON the ground: a shadow, a pad, a pool */
    slab(x: number, z: number, r: number, colour: string, rz = r) {
      /* A CAST SHADOW, NOT A DISC UNDER THE FEET (2026-09-05, against the reference renders). The sun in this world is
       * up and to the left (model3d.ts), so a body's shadow falls down and to the right and is longer than it is wide.
       * One offset here moves every creature's shadow at once. */
      const w = world(x + 2.1, 0, z + 1.4), f = flat(w);
      /* A CAST SHADOW IS A SHAPE ON THE GROUND, not a dark outline under the feet: wider than the body, longer than it
       * is wide, and violet rather than black, which is what the light in this world leaves behind. */
      const col = colour.length === 9 ? '#4a3f6a' + colour.slice(7) : colour;
      parts.push({ d: depth(w) - 6, op: ['disc', f[0], f[1], r * 1.3, Math.max(0.7, rz * DK * 2.1), col] });
      return f;
    },
    /** a capsule between two points of the body */
    rod(p: XYZ, q2: XYZ, r: number, colour: string) {
      const wa = world(p[0], p[1], p[2]), wb = world(q2[0], q2[1], q2[2]);
      { const cap3 = (globalThis as any).__m3Capture; if (cap3) cap3.push({ kind: 'rod', a: wa, b: wb, r, colour }); }
      parts.push({ d: (depth(wa) + depth(wb)) / 2, op: ['cap', flat(wa), flat(wb), r, colour] });
    },
    /** a single pixel of the body */
    dot(x: number, y: number, z: number, colour: string) {
      const w = world(x, y, z), f = flat(w);
      { const cap3 = (globalThis as any).__m3Capture; if (cap3) cap3.push({ kind: 'dot', w, colour }); }   // an eye, a hoof: the 3D view gives it a bead
      parts.push({ d: depth(w) + 0.4, op: ['px', f[0], f[1], colour] });
    },
    /** a limb: hip/shoulder to foot/hand, the joint from three-dimensional IK */
    limb(root: XYZ, foot: XYZ, l1: number, l2: number, pole: XYZ, r1: number, r2: number, c1: string, c2: string) {
      const knee = ik3(root, foot, l1, l2, pole);
      api.rod(root, knee, r1, c1);
      api.rod(knee, foot, r2, c2);
      return knee;
    },
    ops(): Op[] { return parts.sort((p, q2) => p.d - q2.d).map((x) => x.op); },
  };
  return api;
}

/** The stars a stunned creature sees: they orbit the head in the world, so
 *  they swing behind it and come back round rather than sliding across. */
function stars(b: ReturnType<typeof stage>, t: number, y: number, r = 5) {
  for (let i = 0; i < 3; i++) b.dot(Math.cos(t / 6 + i * 2.1) * r, y + Math.sin(t / 9 + i) * 0.8, Math.sin(t / 6 + i * 2.1) * r, P.y);
}

function sporeling(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const hop = s.moving ? Math.abs(Math.sin(t * 0.32)) * 2.2 : 0;
  const tilt = Math.sin(t / 15) * 0.8;
  const rising = s.moving ? Math.cos(t * 0.32) * Math.sign(Math.sin(t * 0.32) || 1) : 0;
  const squash = (s.reel > 0 ? 1.5 : 0) + (s.stun > 0 ? 0.6 : 0) + (s.moving && hop < 0.6 ? 0.8 : 0) - (rising > 0.5 ? 0.6 : 0);
  /* THE SHOT IS TELEGRAPHED: in the last thirty frames before it fires the cap swells and tips back, and a ring of
   * spore motes gathers round it, so a player can see it coming and step aside. Enraged, the cap flushes red. */
  const brew = s.cool !== undefined && s.cool > 0 && s.cool <= 30 ? (30 - s.cool) / 30 : 0;
  const capTone = s.enraged ? P.r : P.p;
  b.slab(0, 0.5, 4.4 - hop * 0.3 + brew * 0.8, '#00000026');
  // two stubby legs, tucked in a hop
  for (const side of [-1, 1]) {
    const swing = s.moving ? Math.sin(t * 0.32) * 1.2 * side : 0;
    b.rod([side * 2, 3 + hop, 0], [side * 2.4, hop * 0.4, swing], 1.8, P.P);
  }
  // the stalk, and the cap on top of it
  /* A MUSHROOM, NOT A LOLLIPOP (the reference creature sheet, 2026-09-05). A ball on a stick read as a balloon: what
   * says fungus at twenty-four pixels is a cap WIDER THAN IT IS TALL with a rim standing proud of the gills under it,
   * a stem that flares where it meets the ground, and a spot or two on the crown. */
  b.ball(0, 3.4 + hop * 0.4, 0, 2.6, P.u, 2.2, 1.2);   // the stem's flare at the ground
  b.rod([0, 3 + hop, 0], [tilt * 0.5 - brew * 1.2, 7 + hop - squash - brew * 0.6, -brew * 1.5], 3.4, P.u);
  b.ball(tilt - brew * 1.2, 8.2 + hop - squash + brew * 0.4, -brew * 1.5, 6.0 + squash * 0.6 + brew * 1.8, capTone, 5.0 - squash * 0.5 + brew * 0.8, 3.6);
  if (brew > 0.2) for (let i = 0; i < 5; i++) b.dot(Math.cos(t / 3 + i * 1.26) * (6 + brew * 3), 9 + hop + Math.sin(t / 4 + i) * 1.5, Math.sin(t / 3 + i * 1.26) * (6 + brew * 3), i % 2 ? P.e : P.P);
  b.ball(tilt, 7.2 + hop - squash, 0.4, 5.2, P.P, 3.4, 1.2);     // the gills, in the shade under the cap's rim
  for (let i = -1; i <= 1; i++) b.rod([tilt + i * 2.2, 7.3 + hop - squash, -1.4], [tilt + i * 2.6, 7.3 + hop - squash, 2.4], 0.5, P.P);   // and the gills themselves, radiating
  b.dot(tilt - 2, 10.4 + hop - squash, -1, P.w);
  b.dot(tilt + 1.6, 10 + hop - squash, -1.6, P.w);
  b.dot(tilt + 0.4, 11 + hop - squash, 0.6, P.w);
  // eyes on the stalk, on the side it faces
  b.dot(-1, 5.5 + hop, 2.2, P.d); b.dot(1, 5.5 + hop, 2.2, P.d);
  if (s.stun > 0) stars(b, t, 12 + hop);
  return b.ops();
}

function bramble(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const ph = s.moving || s.alight > 0 ? t * (s.alight > 0 ? 0.5 : 0.35) : 0;
  const squash = s.reel > 0 ? 1.2 : 0;
  const by = 6 - squash * 0.5;
  b.slab(0, 0.5, 5.2, '#00000026');
  // four legs at the corners, alternating pairs
  for (let i = 0; i < 4; i++) {
    const side = i < 2 ? -1 : 1, front = i % 2 ? 1 : -1;
    const lift = Math.max(0, Math.sin(ph + (i % 2 ? Math.PI : 0))) * 1.6;
    const step = s.moving ? Math.sin(ph + (i % 2 ? Math.PI : 0)) * 1.4 : 0;
    b.limb([side * 3.5, by - 1.5, front * 1.5], [side * 5.2, lift, front * 2.6 + step],
      2.8, 2.8, [side, 0.2, 0], 1.6, 1.4, P.G, P.d);
  }
  // the body, and the light on its back
  b.ball(0, by, 0, 5.5 - squash * 0.3, P.G, 4.6 - squash);
  b.ball(-1, by + 1.4, -0.6, 3.2, P.g, 2.4);
  /* A THICKET HAS A BROKEN EDGE (the reference creature sheet, 2026-09-05: the mother read as one smooth dark ball
   * with spikes stuck in it). Five lumps of growth around the body, alternating lit and shaded, so the silhouette
   * is foliage rather than a sphere -- and at the mother's 2.5x they are the mass a great beast wants. */
  for (let i = 0; i < 5; i++) {
    const a2 = (i / 5) * Math.PI * 2 + 0.9;
    b.ball(Math.cos(a2) * 4.2, by + 1.2 + Math.sin(i * 1.7) * 1.2, Math.sin(a2) * 3.8, 2.4, i % 2 ? P.g : P.G, 2.0, 2.0);
  }
  // the thorns: a hemisphere of them, not an arc drawn on the screen
  const spikes = 9;
  for (let i = 0; i < spikes; i++) {
    const az = (i / spikes) * Math.PI * 2 + 0.3;
    const el = 0.5 + Math.sin(i * 2.3) * 0.35 + (s.alight > 0 ? Math.sin(t / 3 + i) * 0.12 : 0);
    const L = s.alight > 0 ? 3.6 + ((t + i * 7) % 6) * 0.4 : s.enraged ? 4.4 : 3;   // enraged, every thorn stands out
    const root: XYZ = [Math.cos(az) * 4.6, by + Math.sin(el) * 3.4, Math.sin(az) * 4.2];
    const tip: XYZ = [root[0] + Math.cos(az) * L * 0.8, root[1] + Math.sin(el) * L, root[2] + Math.sin(az) * L * 0.8];
    b.rod(root, tip, 1.5, s.alight > 0 ? ((t + i) % 3 ? P.y : P.r) : P.g);
    b.dot(tip[0], tip[1], tip[2], s.alight > 0 ? P.e : (s as any).big ? '#9ccf5a' : P.d);   // the mother's thorn tips take the lit lime, so she reads on her own green (the art lens, round 47)
  }
  // the berry eye, at the front of the body
  b.ball(0, by + 0.4, 4.2, s.enraged ? 2 : 1.6, P.r, s.enraged ? 1.8 : 1.4);
  b.dot(0.5, by + 0.9, 4.8, s.enraged ? P.y : P.e);
  /* ...AND THE MOTHER WEARS BLOOMS (the reference creature sheet): three open red flowers on her back, each with a
   * pale heart. Two berries at her rump made a great beast read as a hedge with two currants on it. */
  if ((s as any).big) {
    b.ball(-1.2, by + 0.6, -4.0, 1.3, P.r, 1.1); b.ball(1.2, by + 0.6, -4.0, 1.3, P.r, 1.1);
    for (let i = 0; i < 3; i++) {
      const a2 = 0.7 + i * 2.1;
      const bx = Math.cos(a2) * 3.4, bz = Math.sin(a2) * 3.0, byy = by + 3.9;
      b.ball(bx, byy, bz, 1.9, P.r, 1.8, 1.8);            // an open bloom, round: flattened it read as a red smear across her back
      b.ball(bx * 0.9, byy + 0.9, bz * 0.9, 0.8, P.e, 0.8, 0.8);
    }
  }   // ...and two more at the back: the mother has an eye on every face (the art lens, round 47)
  if (s.stun > 0) stars(b, t, by + 7);
  return b.ops();
}

function wisp(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const knocked = s.reel > 0 ? 1 : s.stun > 0 ? 0.7 : 0;   // a knock folds a wisp: it drops, shrinks and its tails close
  const hover = 8 + Math.sin(t / 9) * 1.2 - knocked * 3.5;
  const pulse = (0.5 + 0.5 * Math.sin(t / 5)) * (1 - knocked * 0.6);
  b.slab(0, 0, 3.2 - pulse * 0.4, '#00000022');
  // three tails, trailing behind it in the world and waving
  for (let i = 0; i < 3; i++) {
    const wob = Math.sin(t / 4 + i * 2) * 1.6;
    const spread = (i - 1) * 2.6 * (1 - knocked * 0.7);
    b.rod([0, hover, -2], [spread + wob * 0.4, hover + Math.sin(t / 5 + i) * 1.2, -(5 + i * 2)], 2.4 - i * 0.5, i === 2 ? P.K : P.t);
  }
  b.ball(0, hover, 0, 3.4 + pulse * 0.4 - knocked * 0.8, P.t, 3.4 + pulse * 0.4 - knocked * 0.8);
  b.ball(0, hover, 1.4, 1.8 - knocked * 0.6, P.w, 1.8 - knocked * 0.6);
  if (s.stun > 0) stars(b, t, hover + 5);
  b.dot(-1, hover + 0.5, 3, P.d); b.dot(1, hover + 0.5, 3, P.d);
  // motes, orbiting
  for (let i = 0; i < 3; i++) {
    b.dot(Math.cos(t / 11 + i * 2.1) * 6, hover + Math.sin(t / 13 + i) * 2.5, Math.sin(t / 11 + i * 2.1) * 6, i === 1 ? P.w : P.t);
  }
  return b.ops();
}


/** THE GUARD'S PLATE, FOR THE 3D VIEW (2026-09-22). An armoured kind's painter marks the side it blocks from with a bone
 * bar edged in steel ahead of the body (Enemy.drawTrim: the plate the spear counts double through). The shipped 3D view
 * never runs the painter for a body with a plan, so the tell was gone: a shellback, tidecrab or screehopper faced you
 * with nothing to say which side was the plate. The same bar, laid on the ground ahead of the body, in the plan. */
function guardPlate(b: { rod: (p: XYZ, q: XYZ, r: number, colour: string) => void }, z: number, half = 5) {
  if (!(globalThis as any).__m3Capture) return;   /* the 3D body only: the 2D painter draws its own marker over the sprite (drawTrim), and this plan is the sprite's too */
  /* held UP, not laid down: on the ground the bar sat in the body's cast shadow with a top face a pixel wide, and the
   * camera saw a black slab (the first cut). Two units up its lit top shows and its shadow falls beside it. */
  b.rod([-half, 2.2, z], [half, 2.2, z], 1.5, UI.bone);
  b.rod([-half + 1, 2.9, z + 0.6], [half - 1, 2.9, z + 0.6], 0.5, UI.steel);
}
function shellback(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  // Jade scutes and warm exposed skin distinguish the small shell from terrain rocks.
  // Keep the old shell's established boss palette and proportions.
  const big = !!(s as any).big;
  const shell = big ? P.N : '#607e71', crown = big ? P.n : '#a2b79a';
  const skin = big ? P.n : '#c3a878', limb = big ? P.b : '#8f7957';
  const groove = big ? P.d : '#283c35';
  if (s.flipped > 0) {
    // on its back: the shell under it, the pale belly up, the legs waving
    b.slab(0, 0.5, 7, '#00000026');
    b.ball(0, 3.4, 0, 7, shell, 5.5);
    b.ball(0, 5.2, 0, 5.8, P.u, 4.4);
    b.ball(0, 6, 0, 3, skin, 2.4);
    for (let i = 0; i < 4; i++) {
      const side = i < 2 ? -1 : 1, front = i % 2 ? 1 : -1;
      const wave = Math.sin(t / 5 + i * 1.7) * 1.4;
      b.rod([side * 3.4, 5.5, front * 2.4], [side * 4.6 + wave * 0.3, 9.5 + wave, front * 3], 1.6, limb);
    }
    b.ball(0, 5, 6.5, 2, skin, 1.8); b.dot(0, 5.4, 7.6, P.d);
    stars(b, t, 12);
    return b.ops();
  }
  const ph = s.moving ? t * 0.22 : 0;
  const by = 5.5;
  b.slab(0, 0.5, 7.2, '#00000026');
  // four legs
  for (let i = 0; i < 4; i++) {
    const side = i < 2 ? -1 : 1, front = i % 2 ? 1 : -1;
    const lift = s.moving ? Math.max(0, Math.sin(ph + (i % 2 ? Math.PI : 0))) * 1.2 : 0;
    const step = s.moving ? Math.sin(ph + (i % 2 ? Math.PI : 0)) : 0;
    const foot = side * (big ? 6.4 : 7.7);
    b.rod([side * 4, by - 2.5, front * 1.5], [foot, lift, front * 3.4 + step], 1.8, limb);
    if (!big) b.ball(foot, lift + 0.5, front * 3.4 + step, 1.2, skin, 1.5, 0.7);
  }
  if (!big) guardPlate(b, 9);   // the plate it blocks from, ahead of the shell (a beast's shell is its own plate)
  // the shell: a dome with plates and a rim
  b.ball(0, by - 1.5, 0, 7.6, groove, 6.4, 2.2);              // the rim, standing proud of the legs
  /* A DOME, NOT A DISC (the reference creature sheet, 2026-09-05: ours read as a stone lying on four legs). The shell
   * stands up off the body, and a ROW OF SCUTES round its rim is what says shell rather than rock at this size. */
  b.ball(0, by, 0, 7.2, shell, 5.8, 4.4);                    // the dome: wide, and high enough to be a shell
  for (let i = 0; i < 8; i++) { const a2 = (i / 8) * Math.PI * 2; b.ball(Math.cos(a2) * 6.4, by - 0.6, Math.sin(a2) * 5.2, 1.5, shell, 1.3, 1.0); }
  b.ball(-0.6, by + 2.4, -0.6, 4.6, crown, 3.4, 2.6);        // the lit crown of it
  /* A SEAM IS A GROOVE. Drawn in the shell's own mid tone they sorted ON TOP
   * of the dome and read as a pale bar standing up out of the animal -- in a
   * screenshot it looked like a post planted in a rock, not like a turtle.
   * Cut them in the dark tone and keep them short, so they are lines in the
   * shell rather than things on it. */
  /* ...AND THE SEAMS RUN OUT FROM THE CROWN LIKE A TORTOISE'S SCUTES. Two crossing rods scale up on the old shell
   * into a black PLUS SIGN painted across her back (the reference creature sheet, 2026-09-05); six short seams from
   * the crown to the rim, and a ring around the crown, read as plates at both sizes. */
  for (let i = 0; i < 6; i++) {
    const a2 = (i / 6) * Math.PI * 2 + 0.4;
    b.rod([Math.cos(a2) * 2.2, by + 3.0, Math.sin(a2) * 1.8], [Math.cos(a2) * 6.0, by + 0.6, Math.sin(a2) * 4.8], 0.55, groove);
  }
  for (let i = 0; i < 8; i++) {
    const a2 = (i / 8) * Math.PI * 2, a3 = ((i + 1) / 8) * Math.PI * 2;
    b.rod([Math.cos(a2) * 2.4, by + 3.0, Math.sin(a2) * 2.0], [Math.cos(a3) * 2.4, by + 3.0, Math.sin(a3) * 2.0], 0.5, groove);
  }
  b.dot(-2, by + 3.6, -1.5, P.u);
  // the head: out when walking, pulled in when hit
  const out = (s.reel > 0 || s.stun > 0) ? 0.3 : s.moving ? 1 : 0.7;
  const hz = 5 + 3 * out, bob = s.moving ? Math.sin(ph) * 0.6 : 0;
  const hr = big ? 3.2 : 2.6;
  if (big) b.rod([0, by - 1 + bob, 3], [0, by - 2 + bob, hz - 1], 2.2, P.N);   // the neck out of the shell
  b.ball(0, by - 2 + bob, hz, hr, skin, big ? 2.8 : 2.2, big ? 2.8 : 2.2);
  if (big) for (const side of [-1, 1]) { b.ball(side * 2.3, by - 2.4 + bob, hz + 1.6, 0.85, P.u); b.ball(side * 2.3, by - 2.4 + bob, hz + 1.7, 0.5, P.d); b.ball(side * 2.5, by - 2.6 + bob, hz + 1.9, 0.4, P.w); }
  else { b.dot(-1, by - 1.6 + bob, hz + 1.8, P.d); b.dot(1, by - 1.6 + bob, hz + 1.8, P.d); }
  if (s.stun > 0) stars(b, t, by + 7);
  return b.ops();
}

function charger(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const charging = s.state === 'charge', winding = s.state === 'wind';
  const ph = charging ? t * 0.55 : s.moving ? t * 0.3 : 0;
  const stretch = charging ? 1.6 : 0;
  const by = 6.5 + (winding ? -0.8 : 0) + (charging ? Math.abs(Math.sin(ph)) * 0.8 : 0);
  b.slab(0, 0, 6.5 + stretch, '#00000026', 4);
  // four legs, galloping: the pairs alternate front and back
  for (let i = 0; i < 4; i++) {
    const back = i < 2, side = i % 2 ? 1 : -1;
    const phase = ph + (back ? Math.PI : 0) + (i % 2 ? 0.4 : 0);
    const gait = (charging || s.moving) ? Math.sin(phase) : 0;
    const lift = (charging || s.moving) ? Math.max(0, Math.cos(phase)) * (charging ? 2.2 : 1.2) : 0;
    const rootZ = back ? -4 : 3.5;
    let footZ = rootZ + gait * (2.5 + stretch);
    if (winding && !back && side > 0) footZ = rootZ + 1.5;      // pawing the ground
    b.limb([side * 2.6, by - 2, rootZ], [side * 3, lift, footZ], 2.6, 2.6, [0, 0.2, back ? -1 : 1], 2, 1.8, back ? P.C : P.b, P.d);
  }
  // the body: rump to shoulders, a low head with tusks
  const stride = charging ? Math.abs(Math.sin(ph)) * 1.5 : 0;
  b.rod([0, by, -(4.5 + stride)], [0, by + 0.5, 4 + stride * 0.5], 6.5 - stride * 0.4, P.C);
  b.ball(0, by + 1.5, -3, 3.2, P.b, 2.6);                      // the rump's bristle
  /* THE SHOULDER HUMP AND A BRISTLE RIDGE (the reference creature sheet, 2026-09-05). This was one solid dark rod
   * from rump to neck, which at the sow's scale is a black bar laid along her back: it flattened the whole animal.
   * A boar's line rises to the shoulder and its ridge is a row of coarse hairs with the lit hide showing between. */
  b.ball(0, by + 2.2, 1.6, 3.4, P.C, 2.8, 2.4);                // the shoulders, standing above the rump
  for (let i = 0; i < 6; i++) {
    const z = -4.6 + i * 1.6, h = by + 3.2 + Math.max(0, 1.4 - Math.abs(z - 1) * 0.24);
    b.rod([0, h - 0.9, z], [(i % 2 ? 0.5 : -0.5), h + 0.7, z - 0.4], 0.75, P.d);
  }
  const hz = 7 + (charging ? 1.5 : 0), hy = by - (winding ? 2.5 : 1.2);
  b.ball(0, hy, hz, 3.4, P.b, 2.9, 3.1);
  for (const side of [-1, 1]) {
    b.rod([side * 1.5, hy - 1, hz + 1.5], [side * 2.2, hy + 0.8, hz + 4.2], 1.2, P.e);   // tusks
    b.rod([side * 2.2, hy + 2.5, hz - 1.5], [side * 3, hy + 4.2, hz - 2.5], 1.4, P.C);   // ears
  }
  /* A FACE THAT SCALES (the art lens, round 40: the sow was 'a brown mound with two tusks'). Eyes and a snout as small
   * balls, not dots: a dot stays one pixel at the sow's 2.3x, a ball grows with it; the white glint rounds to nothing
   * on the boar and to a pixel on the sow. */
  const big = !!(s as any).big;   // set by scaledPlan: the plan is built in the boar's cell under the boar's own type and scaled after; a glint on a fourteen-pixel boar is a white speck
  for (const side of [-1, 1]) {
    // ON the head's surface, not inside it: at z = hz + 2.4 the head ball (r 3.4) is 2.4 wide, so an eye at x 1.2 was buried
    if (big) b.ball(side * 2.5, hy + 0.7, hz + 2.3, 0.85, P.e);   // a cream ring round the eye, so a dark eye reads on a dark hide from the side
    b.ball(side * 2.5, hy + 0.7, hz + 2.4, 0.55, P.d);
    if (big) b.ball(side * 2.7, hy + 0.5, hz + 2.6, 0.5, P.w);
  }
  b.ball(0, hy + 1.9, hz + 3.6, 1.05, P.d, 0.8, 0.9);   // the snout
  if (winding) for (let i = 0; i < 2; i++) b.dot((i - 0.5) * 2, 1, hz + 1 + (t % 4) * 0.5, P.u);
  if (charging) for (let i = 0; i < 3; i++) b.dot((i - 1) * 2, 1 + (i % 2), -(7 + i * 2.5), P.u);
  if (s.stun > 0) stars(b, t, by + 8);
  return b.ops();
}

/* THE GREAT BEASTS (LS-225): each is a small creature's own plan, built in
 * that creature's cell and scaled about its floor line into a 64-pixel
 * cell, in its own hide. The same skeleton at a bigger size is the point: a
 * player who has fought boars knows what the sow's wind-up means, and one
 * who has met a shellback knows the old shell blocks from the front. The
 * builder derives lit and shaded tones from a chunk's colour, so a map of
 * the plan's colours misses every tone it made -- anything not in the map
 * is pulled toward the hide instead, with a few colours kept as they are
 * (tusks, thorn tips, shadows). */
function hideTone(map: Record<string, string>, hide: number, keep: string[]) {
  return (c: string): string => {
    if (map[c]) return map[c];
    if (c.length !== 7 || keep.includes(c)) return c;   // a translucent shadow, or a kept colour
    const a = parseInt(c.slice(1), 16);
    const ch = (sh: number) => Math.round(((a >> sh) & 255) * 0.45 + ((hide >> sh) & 255) * 0.55);
    return '#' + [16, 8, 0].map(sh => ch(sh).toString(16).padStart(2, '0')).join('');
  };
}
function scaledPlan(plan: (s: CreatureState, C: number, F: number) => Op[], type: string, c0: number, f0: number, k: number, col: (c: string) => string) {
  return (s: CreatureState, C: number, F: number): Op[] => {
    { const cap3 = (globalThis as any).__m3Capture; if (cap3) cap3.push({ kind: 'scaled', k, col }); }   // a probe or the 3D view: the beast is this plan scaled and recoloured (2026-09-13)
    const ops = plan({ ...s, type, big: true } as any, c0, f0);   // the scaled body knows it is big (the sow's face)
    const sx = (x: number) => C + (x - c0) * k, sy = (y: number) => F - (f0 - y) * k;
    return ops.map((op) => op[0] === 'cap' ? ['cap', [sx(op[1][0]), sy(op[1][1])], [sx(op[2][0]), sy(op[2][1])], op[3] * k, col(op[4])] as Op
      // the ground shadow stays a shadow: wider with the body, but not taller, or it climbs the belly
      : op[0] === 'disc' ? ['disc', sx(op[1]), op[5].length > 7 ? F + 1 : sy(op[2]), op[3] * k, op[4] * (op[5].length > 7 ? 1.3 : k), col(op[5])] as Op
      : ['disc', sx(op[1]), sy(op[2]), k / 2, k / 2, col(op[3])] as Op);
  };
}
const sow = scaledPlan(charger, 'charger', 14, 21, 2.3, hideTone({ [P.C]: '#4a2e26', [P.b]: '#2f1c18', [P.d]: '#16100c', [P.e]: '#e6dcc4' }, 0x3a2018, ['#e6dcc4', P.w]));   // the tusks and the eyes' glint keep their white through the hide
const oldshell = scaledPlan(shellback, 'shellback', 13, 20, 2.4, hideTone({ [P.N]: '#3e4a3a', [P.n]: '#2a3328', [P.b]: '#5a4a34', [P.u]: '#d8d0b8' }, 0x2c3628, ['#d8d0b8', P.w]));
const bramblemother = scaledPlan(bramble, 'bramble', 12, 19, 2.5, hideTone({ [P.G]: '#2e4a22', [P.g]: '#1d3016', [P.d]: '#101a0c', [P.r]: '#c8402a' }, 0x4a6a34, ['#c8402a', '#9ccf5a']));   // a lighter rim and the lime tips kept through the hide (the art lens, round 47: on her own heath she was a dark green blob)

/* THE THREE NEW KINDS GET BONES TOO (LS-293, the one gap the card left): the gloam, the cinder and the eel were
 * drawn from generated frames while every other body was posed from its state and painted here. Now they are
 * posed like the rest -- a moth that beats its wings and hovers, a beetle whose legs step and whose embers pulse,
 * an eel that swims as a wave down its body -- so the whole roster is one kind of thing under one light. */
function gloam(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const knocked = s.reel > 0 ? 1 : s.stun > 0 ? 0.6 : 0;
  const hover = 6.5 + Math.sin(t / 7) * 1.0 - knocked * 4;
  const flap = Math.sin(t * 0.85) * (1 - knocked * 0.7);   // the beat, fast
  b.slab(0, 0, 3.0, '#0000001e');
  // the body: a dark fuzzy rod along its length, the head at the front
  b.rod([0, hover, -2.2], [0, hover + 0.2, 2.2], 1.4, P.d);
  b.ball(0, hover + 0.4, 2.6, 1.3, P.d, 1.2, 1.1);
  b.dot(-0.7, hover + 0.9, 3.4, P.y); b.dot(0.7, hover + 0.9, 3.4, P.y);
  b.rod([-0.6, hover + 1.2, 3.2], [-2.2, hover + 3.4, 4.6], 0.4, P.d); b.rod([0.6, hover + 1.2, 3.2], [2.2, hover + 3.4, 4.6], 0.4, P.d);   // the antennae
  // the wings: a big fore-wing and a smaller hind-wing a side, tilting on the beat (the span foreshortens as they rise)
  for (const side of [-1, 1]) {
    const reach = 2.4 + 2.2 * (1 - Math.abs(flap) * 0.55), lift = flap * 2.2;
    b.ball(side * reach, hover + 0.6 + lift, 0.6, 3.6, P.m, 2.4, 1.1);
    b.ball(side * (reach - 0.6), hover + 0.2 + lift * 0.7, -2.4, 2.6, P.M, 1.8, 0.9);
    b.dot(side * (reach + 0.8), hover + 0.9 + lift, 0.8, P.w);   // the eyespot
  }
  if (s.stun > 0) stars(b, t, hover + 5);
  return b.ops();
}

function cinder(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const step = s.moving ? Math.sin(t * 0.45) : 0;
  const squash = s.reel > 0 ? 1.2 : 0;
  const glow = 0.5 + 0.5 * Math.sin(t / 4);   // the embers pulse
  b.slab(0, 0.5, 5.2, '#00000026');
  // six legs, three a side, stepping in two sets
  for (let i = 0; i < 3; i++) for (const side of [-1, 1]) {
    const phase = (i % 2 ? 1 : -1) * side * step;
    const z = (i - 1) * 2.6 + phase * 1.2;
    b.rod([side * 3.2, 2.4, (i - 1) * 2.6], [side * 6.8, 0.2, z], 1.3, '#775042');
    b.dot(side * 6.8, 0.5, z, '#ce8150');
  }
  // the shell: a dark dome split down the back, the head low at the front
  b.ball(0, 3.2 - squash * 0.4, -0.4, 5.2 + squash * 0.5, P.d, 4.0, 2.8 - squash * 0.6);
  b.rod([0, 5.6 - squash * 0.6, -4], [0, 5.6 - squash * 0.6, 3], 0.8, '#b84e32');   // ember seam remains visible between the dark wing cases
  b.ball(0, 2.4, 4.4, 2.0, P.d, 1.7, 1.3);
  b.dot(-1, 3.0, 5.6, P.y); b.dot(1, 3.0, 5.6, P.y);
  b.rod([-0.8, 3.6, 5.4], [-2.8, 5.6, 6.6], 0.8, '#775042'); b.rod([0.8, 3.6, 5.4], [2.8, 5.6, 6.6], 0.8, '#775042');
  // the embers under the shell: four, red and gold, swapping on the pulse; enraged they all burn gold
  const spots: [number, number][] = [[-2.4, -1.6], [2.4, -1.6], [-1.8, 1.8], [1.8, 1.8]];
  spots.forEach(([x, z], i) => b.dot(x, 5.2 - squash * 0.5, z, s.enraged || (i % 2 === 0 ? glow > 0.5 : glow <= 0.5) ? P.y : P.r));
  if (s.stun > 0) stars(b, t, 9);
  return b.ops();
}

function eel(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const knocked = s.reel > 0 ? 1 : 0;
  b.slab(0, 0, 4.0, '#0000001e', 2.2);
  // a wave down the body: seven beads from tail to head, each swung sideways a little behind the one before
  for (let i = 0; i < 7; i++) {
    const z = -7 + i * 2.3, sway = Math.sin(t * 0.5 - i * 0.9) * (s.moving ? 1.8 : 0.7);
    const r = 1.2 + Math.min(i, 6 - i) * 0.3 + (i === 6 ? 0.6 : 0);
    b.ball(sway, 1.3 + knocked, z, r, i === 6 ? P.C : P.b, r * 0.9, r * 0.8);
    if (i >= 2 && i <= 4) b.dot(sway, 0.8 + knocked, z, P.s);   // the sand stripe along the belly's edge
  }
  const hx = Math.sin(t * 0.5 - 6 * 0.9) * (s.moving ? 1.8 : 0.7);
  b.dot(hx - 0.8, 2.2 + knocked, 7.4, P.y); b.dot(hx + 0.8, 2.2 + knocked, 7.4, P.y);   // the eyes on top of the head
  if (s.stun > 0) stars(b, t, 8);
  return b.ops();
}


/* SIX MORE BODIES, FOR THE 3D VIEW ONLY (LS-416, 2026-09-22). The tidecrab, mossback, sunbasker, screehopper, stormgull and
 * duskling were authored as 16x16 pixel poses (src/sprites) after the plans above were written, and had no plan -- so in
 * the shipped 3D view, where every body is its plan's solids, these six stood as flat billboards among meshes. Each gets a
 * plan built from its own sprite's silhouette and letters (the sprite's palette IS the P palette), sized to the sprite
 * (test/checks/creaturemesh.ts holds the mesh's height, width and area to the sprite's). The 2D view keeps the authored
 * pixels: these plans are reached through `creatureOps` only, never `hasRig`, so nothing about the flat game changes. */

/** The same stage, every length multiplied: the authored sprites draw at about one and a half times their 16-pixel grid,
 * and a plan written in grid units comes out small against them (the tidecrab at 0.6x its sprite's height). */
function magnify(b: ReturnType<typeof stage>, k: number) {
  return {
    ball: (x: number, y: number, z: number, r: number, colour: string, rz = r, ry = r) => b.ball(x * k, y * k, z * k, r * k, colour, rz * k, ry * k),
    rod: (p: XYZ, q2: XYZ, r: number, colour: string) => b.rod([p[0] * k, p[1] * k, p[2] * k], [q2[0] * k, q2[1] * k, q2[2] * k], r * k, colour),
    dot: (x: number, y: number, z: number, colour: string) => b.dot(x * k, y * k, z * k, colour),
    slab: (x: number, z: number, r: number, colour: string, rz = r) => b.slab(x * k, z * k, r * k, colour, rz * k),
    ops: () => b.ops(),
  };
}
function tidecrab(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const K = 1.5; const b0 = stage(s.dir, C, F); const b = magnify(b0, K);
  const shell = P.N, top = P.n, pale = P.m, eye = P.e, leg = P.N;
  if (s.flipped > 0) {
    b.slab(0, 0.5, 6, '#00000026');
    b.ball(0, 2.4, 0, 6.5, shell, 4.2, 2);
    b.ball(0, 3.2, 0, 4.6, top, 3, 1.4);
    for (let i = 0; i < 6; i++) { const side = i < 3 ? -1 : 1, k = i % 3; const wave = Math.sin(t / 5 + i * 1.3) * 1.2; b.rod([side * 4, 3.5, (k - 1) * 2.2], [side * 6.5 + wave * 0.4, 6.5 + wave, (k - 1) * 2.6], 1, leg); }
    stars(b0, t, 8 * K);
    return b.ops();
  }
  const ph = s.moving ? t * 0.3 : 0;
  b.slab(0, 0.5, 6.5, '#00000026');
  guardPlate(b, 8, 4);   // the plate, ahead of the claws
  // the carapace: a flat wide oval, its top a shade lighter, a row of pale bumps along the front
  b.ball(0, 2.2, 0, 6.2, shell, 3.6, 2.1);
  b.ball(0, 3, -0.3, 4.6, top, 2.6, 1.3);
  for (let i = -2; i <= 2; i++) b.dot(i * 1.9, 3.4, 2.6, pale);
  // eye stalks, forward
  for (const side of [-1, 1]) { b.rod([side * 2.2, 3, 2.4], [side * 2.4, 5.6, 3.2], 0.55, shell); b.dot(side * 2.4, 6, 3.4, eye); }
  // two claws, forward and out, the far one raised a little as it scuttles
  for (const side of [-1, 1]) {
    const raise = s.moving ? Math.max(0, Math.sin(ph + (side > 0 ? Math.PI : 0))) * 0.8 : 0;
    b.rod([side * 4.5, 2, 2], [side * 7, 2.4 + raise, 4.4], 1, leg);
    b.ball(side * 7.4, 2.6 + raise, 5.2, 1.7, shell, 1.3, 1.1);
    b.dot(side * 8, 2.8 + raise, 6, top);
  }
  // three legs a side, the scuttle alternating pairs
  for (let i = 0; i < 6; i++) {
    const side = i < 3 ? -1 : 1, k = i % 3;
    const lift = s.moving ? Math.max(0, Math.sin(ph + ((i + k) % 2 ? Math.PI : 0))) * 1.1 : 0;
    b.rod([side * 5, 1.8, (k - 1) * 2.2], [side * 7.6, 0.2 + lift, (k - 1) * 2.8], 0.8, leg);
  }
  if (s.stun > 0) stars(b0, t, 7 * K);
  return b.ops();
}

function mossback(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const K = 1.3; const b0 = stage(s.dir, C, F); const b = magnify(b0, K);
  const hide = P.G, moss = P.g, pale = P.m, eye = P.e, wet = P.k;
  const ph = s.moving ? t * 0.2 : 0;
  b.slab(0, 0.5, 5.6, '#00000026');
  // four stubby legs under a low dome
  for (let i = 0; i < 4; i++) {
    const side = i < 2 ? -1 : 1, front = i % 2 ? 1 : -1;
    const step = s.moving ? Math.sin(ph + (i % 2 ? Math.PI : 0)) * 0.9 : 0;
    b.rod([side * 3.2, 2, front * 2.2], [side * 4.6, 0.2, front * 2.6 + step], 1.1, hide);
  }
  // the dome, mossed over on top, a few pale flecks
  b.ball(0, 2.6, 0, 5.4, hide, 4.2, 2.6);
  b.ball(0, 3.6, -0.4, 4.4, moss, 3.2, 1.8);
  for (let i = 0; i < 3; i++) b.dot(-2 + i * 2, 4.8, -0.6 + (i % 2), pale);
  // the head, low and forward, with two bright eyes
  const bob = s.moving ? Math.sin(ph * 2) * 0.3 : 0;
  b.ball(0, 2.2 + bob, 5, 2, hide, 1.8, 1.5);
  b.dot(-1, 3 + bob, 6, eye); b.dot(1, 3 + bob, 6, eye);
  // a drop off its flank: it lives wet
  if (s.wet || (t >> 3) % 4 === 0) b.dot(-4.6, 1.2, 1, wet);
  if (s.stun > 0) stars(b0, t, 6 * K);
  return b.ops();
}

function sunbasker(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const hide = P.C, band = P.c, belly = P.s, eye = P.e;
  const ph = s.moving ? t * 0.28 : 0;
  b.slab(0, 0.3, 6.5, '#00000026');
  // a long low body from tail to snout, banded along the back
  const sway = s.moving ? Math.sin(ph) * 0.8 : 0;
  b.rod([sway * 0.4, 1.4, -6.5], [0, 1.6, 3.5], 1.9, hide);
  b.rod([0, 1.1, -5], [0, 1.2, 3], 1.5, belly);
  for (let i = -2; i <= 2; i++) b.dot(0, 2.9, i * 1.6, band);
  // the tail, thin, trailing and swinging as it runs
  b.rod([sway * 0.4, 1.4, -6.5], [sway * 1.6, 1, -10], 0.8, hide);
  // the head: a wedge with a bright eye either side
  b.ball(0, 1.6, 4.8, 2.2, hide, 2.4, 1.3);
  b.dot(-1.4, 2.2, 5, eye); b.dot(1.4, 2.2, 5, eye);
  // four legs, splayed out to the sides, the diagonal pairs stepping together
  for (let i = 0; i < 4; i++) {
    const side = i < 2 ? -1 : 1, front = i % 2 ? 1 : -1;
    const step = s.moving ? Math.sin(ph + ((i === 0 || i === 3) ? 0 : Math.PI)) * 1.2 : 0;
    /* splayed wide (the sprite is seventeen across and ten tall) and BENT at the knee, out and forward, the way a lizard's
     * legs go: a straight rod from hip to toe read as an aeroplane on the sheet */
    const kx = side * 4.6, kz = front * 2.4 + step * 0.5 + front * 1.2;
    b.rod([side * 1.6, 1.4, front * 2.4], [kx, 2.2, kz], 0.9, hide);
    b.rod([kx, 2.2, kz], [side * 7.2, 0.2, front * 2.6 + step], 0.8, hide);
    b.dot(side * 7.4, 0.2, front * 2.6 + step, band);
  }
  if (s.stun > 0) stars(b, t, 5);
  return b.ops();
}

function screehopper(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const shell = P.N, pale = P.n, rim = P.m, eye = P.e;
  if (s.flipped > 0) {
    b.slab(0, 0.5, 5, '#00000026');
    b.ball(0, 2.4, 0, 4.4, shell, 3.4, 2.2);
    b.ball(0, 3, 0, 3.2, pale, 2.4, 1.2);
    for (const side of [-1, 1]) { const wave = Math.sin(t / 5 + side) * 1.4; b.rod([side * 2, 3.5, -1], [side * 5, 7 + wave, -3], 0.9, shell); b.rod([side * 2, 3.5, 1.5], [side * 4, 6 + wave * 0.5, 3], 0.7, shell); }
    stars(b, t, 8);
    return b.ops();
  }
  // it hops: the body rises on a bounce, the hind legs fold under it at the top
  const hop = s.moving ? Math.abs(Math.sin(t * 0.3)) * 2.6 : 0;
  b.slab(0, 0.5, 5 - hop * 0.3, '#00000026');
  // hind legs: thigh up and back, shin down to the ground, folded like a Z
  for (const side of [-1, 1]) {
    const fold = hop * 0.35;
    b.rod([side * 2.2, 2.4 + hop, -1.2], [side * 6.4, 5.2 + hop - fold, -3.2], 1.1, shell);   // the sprite is twenty-three across: the legs make the width
    b.rod([side * 6.4, 5.2 + hop - fold, -3.2], [side * 9, 0.2 + hop * 0.6, -1.2], 0.8, pale);
  }
  // front legs, small
  for (const side of [-1, 1]) b.rod([side * 1.8, 2 + hop, 2], [side * 5.2, 0.2 + hop * 0.7, 3.4], 0.7, shell);
  guardPlate(b, 7, 4);   // the plate, ahead of the head
  // the body: a plated hump, a pale stripe down its back and rim
  b.ball(0, 3.2 + hop, -0.2, 4.6, shell, 4.2, 2.8);
  b.ball(0, 4.4 + hop, -0.6, 3.2, pale, 3, 1.3);
  for (let i = -1; i <= 1; i++) b.dot(i * 2.4, 4.2 + hop, 2.6, rim);
  // the head, with the eyes wide apart, and two antennae sweeping back
  b.ball(0, 3.4 + hop, 3.6, 2.4, shell, 1.8, 1.8);
  b.dot(-1.6, 4.2 + hop, 4.4, eye); b.dot(1.6, 4.2 + hop, 4.4, eye);
  for (const side of [-1, 1]) b.rod([side * 1, 5 + hop, 4], [side * 5, 7.5 + hop, 1], 0.4, pale);
  if (s.stun > 0) stars(b, t, 8 + hop);
  return b.ops();
}

function stormgull(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const white = P.w, grey = P.M, dark = P.o, beak = P.y, eye = P.e;
  // it flies: the body rides five units up and the wings beat
  const flap = Math.sin(t * 0.4);
  const ride = 5 + Math.sin(t * 0.2) * 0.5;
  b.slab(0, 0.5, 4.5, '#0000001c');
  // the body, a white spindle, the tail a grey fan behind
  b.rod([0, ride, -3.5], [0, ride + 0.4, 3], 2, white);
  b.ball(0, ride + 0.2, -4.2, 1.6, grey, 1.4, 0.8);
  // the wings: grey, out to the sides, tipped dark, beating together
  for (const side of [-1, 1]) {
    const tip = ride + flap * 3;
    /* a gull's M: the inner wing forward and up, the outer swept back and down to the tip (straight wings read as an aeroplane) */
    /* ...and from this camera, which sees the top face and little of height, the sweep has to be in DEPTH: the tips reach
     * forward, a V opening the way it flies, as the sprite's M does */
    b.rod([side * 1.2, ride + 0.6, 0], [side * 4.8, ride + 1.2 + flap * 1.4, 2.6], 1.4, grey);
    b.rod([side * 4.8, ride + 1.2 + flap * 1.4, 2.6], [side * 8, tip, 5.4], 0.9, grey);
    b.dot(side * 8.4, tip, 5.8, dark);
  }
  // the head, forward, a yellow beak and a bead of an eye each side
  b.ball(0, ride + 0.8, 3.6, 1.8, white, 1.6, 1.5);
  b.rod([0, ride + 0.6, 4.8], [0, ride + 0.4, 6.6], 0.5, beak);
  b.dot(-1.2, ride + 1.2, 4, eye); b.dot(1.2, ride + 1.2, 4, eye);
  // the feet, tucked
  b.dot(-0.8, ride - 1.4, -0.5, beak); b.dot(0.8, ride - 1.4, -0.5, beak);
  if (s.stun > 0) stars(b, t, ride + 4);
  return b.ops();
}

function duskling(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim;
  const b = stage(s.dir, C, F);
  const fur = P.N, belly = P.w, eye = P.e, dark = P.o;
  const ph = s.moving ? t * 0.3 : 0;
  const bob = s.moving ? Math.abs(Math.sin(ph)) * 0.8 : Math.sin(t / 12) * 0.2;
  b.slab(0, 0.5, 3.6, '#00000026');
  // two short legs
  for (const side of [-1, 1]) { const step = s.moving ? Math.sin(ph + (side > 0 ? Math.PI : 0)) * 0.8 : 0; b.rod([side * 1.4, 2 + bob, 0], [side * 1.6, 0.2, step], 0.8, fur); }
  // an upright little body with a pale belly
  b.ball(0, 4.2 + bob, 0, 4.2, fur, 2.8, 3);
  b.ball(0, 3.6 + bob, 1.4, 2.6, belly, 1.4, 1.9);
  // the head, wide-eyed, with two tall ears
  b.ball(0, 7.4 + bob, 0.2, 3.2, fur, 2.4, 2.2);
  b.dot(-1.4, 7.7 + bob, 2.4, eye); b.dot(1.4, 7.7 + bob, 2.4, eye);
  for (const side of [-1, 1]) { b.rod([side * 1.8, 8.8 + bob, 0], [side * 2.2, 10.6 + bob, -0.4], 0.7, fur); b.dot(side * 2.2, 10.8 + bob, -0.4, dark); }
  if (s.stun > 0) stars(b, t, 11);
  return b.ops();
}

/* THE SOW, FOR THE 3D VIEW (2026-09-26). Her body was the boar's plan scaled 2.3 and darkened, and from this camera -- which
 * sees the top face and little of height -- the boar's capsule of a body ran past its low head, so the first great beast of
 * the game read as a dark brown dome with one tusk: the newcomer probe's film of her fight shows nothing else. Her own plan,
 * in the boar's units at the same scale: a leaner body ending behind the shoulders, a big head carried out in front and a
 * little high, long pale tusks curving up, a bristle ridge down the spine and pale ears -- everything a top view can read --
 * in a mid-brown hide so the shading carries the form. Her charge, wind-up and stun keep the boar's rhythm. */
function sowMesh(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim; const K = 2.3; const b0 = stage(s.dir, C, F); const b = magnify(b0, K);
  const charging = s.state === 'charge', winding = s.state === 'wind', spent = s.state === 'spent' || s.stun > 0;
  /* the character sheet's sow (docs/art/concept/r3_3.jpg): a grey-brown bristled hide, a darker ridge, a dusky snout, cream tusks */
  const hide = '#5a4a3e', hideDark = '#3a2e26', ridge = '#4a3a2e', belly = '#6e5a4a', snout = '#b07a6a', tusk = '#f3ecd8', ear = '#6a5444';
  const ph = charging ? t * 0.55 : s.moving ? t * 0.3 : 0;
  const by = 5.8 + (winding ? -0.8 : 0) + (charging ? Math.abs(Math.sin(ph)) * 0.8 : 0) - (spent ? 1.2 : 0);
  b.slab(0, 0, 7, '#00000026', 4.5);
  for (let i = 0; i < 4; i++) {
    const back = i < 2, side = i % 2 ? 1 : -1;
    const phase = ph + (back ? Math.PI : 0) + (i % 2 ? 0.4 : 0);
    const gait = (charging || s.moving) ? Math.sin(phase) : 0, lift = (charging || s.moving) ? Math.max(0, Math.cos(phase)) * (charging ? 2.2 : 1.2) : 0;
    const rootZ = back ? -3.8 : 2.6; let footZ = rootZ + gait * 2.5; if (winding && !back && side > 0) footZ = rootZ + 1.5;
    b.rod([side * 3.2, by - 1.6, rootZ], [side * 3.6, lift, footZ], 1.5, hideDark);
    b.dot(side * 3.6, lift + 0.2, footZ + 0.4, '#241a24');
  }
  // the body: rump to shoulders, ending behind the head, a paler belly under it
  b.rod([0, by, -4.6], [0, by + 0.6, 2.4], 4.9, hide);
  b.ball(0, by - 1.4, -1, 4.3, belly, 3.2, 2);
  b.ball(0, by + 1.6, 1.6, 3.8, hide, 3.2, 2.6);                     // the shoulders' hump
  // the bristle ridge down the spine: pale tufts, tallest over the shoulders
  for (let i = 0; i < 8; i++) { const z = -5 + i * 1.2, h = by + 4.2 + Math.max(0, 1.6 - Math.abs(z - 1.2) * 0.3); b.rod([0, h - 1, z], [(i % 2 ? 0.4 : -0.4), h + 0.8, z - 0.5], 0.8, ridge); }
  // the head, out in front and a little high, big enough to own the front third of her
  const hz = 7.2 + (charging ? 1.6 : 0), hy = by + (winding ? -1.6 : spent ? -1 : 0.2);
  b.ball(0, hy, hz, 3.6, hide, 3.3, 3.1);
  b.ball(0, hy + 0.6, hz + 3.1, 1.7, snout, 1.2, 1.4);               // the snout's disc
  b.dot(-0.6, hy + 0.9, hz + 4.2, '#241a24'); b.dot(0.6, hy + 0.9, hz + 4.2, '#241a24');
  for (const side of [-1, 1]) {
    b.rod([side * 1.6, hy - 0.6, hz + 2.2], [side * 2.8, hy + 1.6, hz + 5], 0.9, tusk);          // tusks: out and up
    b.rod([side * 2.8, hy + 1.6, hz + 5], [side * 2.4, hy + 3.2, hz + 5.6], 0.7, tusk);
    b.rod([side * 2, hy + 2.6, hz - 1.2], [side * 3.6, hy + 4.4, hz - 2.4], 1.1, ear);           // ears, pale, standing
    b.ball(side * 2.2, hy + 1.8, hz + 1.6, 0.8, tusk); b.ball(side * 2.3, hy + 1.9, hz + 1.8, 0.5, '#241a24');   // eyes on the brow, ringed
  }
  if (winding) for (let i = 0; i < 3; i++) b.dot((i - 1) * 2, 0.8, hz + 1 + (t % 4) * 0.5, P.u);
  if (charging) for (let i = 0; i < 4; i++) b.dot((i - 1.5) * 2, 1 + (i % 2), -(7 + i * 2.2), P.u);
  if (s.stun > 0) stars(b0, t, (by + 9) * K);
  return b.ops();
}

/* THE OLD SHELL AS THE CHARACTER SHEET DRAWS HIM (docs/art/concept/r3_3.jpg, 2026-09-28): an ancient tortoise -- a high
 * dome of distinct scutes, each a plate standing a little proud so the ink draws the seams between them, moss on the
 * crown, a wrinkled head and four pillar legs. On his back or stunned the old plan draws him, which knows those states. */
function oldshellMesh(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim; const K = 2.4; const b0 = stage(s.dir, C, F); const b = magnify(b0, K);
  const plate = '#6e7856', plate2 = '#7f8a5e', seam = '#4a5238', moss = '#8fa24c', skin = '#8a8468', skinD = '#6a6450';
  /* ON ITS BACK, AT ITS OWN SIZE (2026-09-29). The flip fell back to the old unscaled body, so a turned shell shrank to
     two fifths of itself; now it is this body upturned: the dome rocking on the ground, the pale plastron up, four legs
     paddling at the air and the head lolled out to one side. */
  if (s.flipped > 0) {   // part for part in the upright plan's order and colours, so the painted in-betweens roll it over (creatures_export lerpPose)
    const rock = Math.sin(t * 0.18) * 0.4, wave = t * 0.45, belly = '#c9b98a', belly2 = '#b5a676';
    b.slab(0, 0.5, 7.4, '#00000026');
    for (let i = 0; i < 4; i++) { const side = i < 2 ? -1 : 1, front = i % 2 ? 1 : -1, w = Math.sin(wave + i * 1.7) * 0.9;
      const paw: XYZ = [side * (6.6 + w * 0.5) + rock, 6.4 + w * 0.8, front * (4.6 - w * 0.4)];   // splayed out past the rim, not straight up at the eye
      b.rod([side * 4.6 + rock, 5.2, front * 3.0], paw, 1.3, skinD); b.ball(paw[0], paw[1] + 0.3, paw[2], 1.1, skin, 1.0, 0.7); }   // legs paddling at the air
    b.ball(rock, 3.8, 0, 7.4, seam, 6.2, 1.4);                                        // the marginal rim
    b.ball(rock, 2.4, 0, 6.2, seam, 5.0, 2.6);                                        // the dome, down on the ground
    for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; b.ball(Math.cos(a) * 5.2 + rock, 3.3, Math.sin(a) * 4.3, 2.2, i % 2 ? plate : plate2, 1.8, 1.4); }   // the costal plates at the rim
    for (let i = 0; i < 5; i++) { const a = (i / 5) * Math.PI * 2 + 0.3; b.ball(Math.cos(a) * 2.6 + rock, 1.2, Math.sin(a) * 2.1, 2.0, i % 2 ? plate2 : plate, 1.7, 1.2); }   // the upper ring, under it now
    b.ball(rock, 0.8, 0, 2.0, plate2, 1.7, 1.0);                                      // the crown scute, underneath
    for (let i = 0; i < 4; i++) b.ball(Math.sin(i * 2.1) * 2.2 + rock, 0.7, Math.cos(i * 1.7) * 1.8, 1.1, moss, 1.0, 0.6);
    const lol = Math.sin(t * 0.07) * 0.6;
    b.rod([rock, 4.0, 5.2], [1.6 + lol, 2.4, 7.6], 1.6, skinD);                       // the neck, slack
    b.ball(1.9 + lol, 2.0, 8.2, 2.0, skin, 1.9, 1.6);                                 // the head, lolled
    b.dot(1.2 + lol, 2.4, 9.6, '#241a24'); b.dot(2.8 + lol, 2.2, 9.4, '#241a24');
    b.ball(rock, 4.9, 0, 6.0, belly, 5.0, 1.1);                                       // the plastron, face up (no upright part: it comes in at the roll's middle)
    for (let i = -1; i <= 1; i++) b.ball(rock, 5.7, i * 2.8, 4.2 - Math.abs(i) * 0.8, belly2, 1.0, 0.35);   // its seams
    return b.ops();
  }
  const ph = s.moving ? t * 0.22 : 0, by = 5.4;
  b.slab(0, 0.5, 7.4, '#00000026');
  for (let i = 0; i < 4; i++) { const side = i < 2 ? -1 : 1, front = i % 2 ? 1 : -1; const lift = s.moving ? Math.max(0, Math.sin(ph + (i % 2 ? Math.PI : 0))) * 1.1 : 0;
    b.rod([side * 4.2, by - 2, front * 2.2], [side * 5.6, lift, front * 3.2], 2.1, skinD); b.ball(side * 5.6, lift + 0.6, front * 3.2, 1.6, skin, 1.4, 0.8); }
  b.ball(0, by - 1.6, 0, 7.4, seam, 6.2, 1.8);                                   // the marginal rim
  b.ball(0, by, 0, 6.2, seam, 5.0, 3.6);                                         // the dome under the plates: its colour is the seams', kept low so every plate stands proud of it
  for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; b.ball(Math.cos(a) * 4.8, by + 1.0, Math.sin(a) * 3.9, 2.2, i % 2 ? plate : plate2, 1.8, 2.0); }   // the costal ring
  for (let i = 0; i < 5; i++) { const a = (i / 5) * Math.PI * 2 + 0.3; b.ball(Math.cos(a) * 2.6, by + 3.2, Math.sin(a) * 2.1, 2.0, i % 2 ? plate2 : plate, 1.7, 1.6); }   // the upper ring
  b.ball(0, by + 4.3, 0, 2.0, plate2, 1.7, 1.5);                                  // the crown scute
  for (let i = 0; i < 4; i++) b.ball(Math.sin(i * 2.1) * 2.2, by + 4.9, Math.cos(i * 1.7) * 1.8, 1.1, moss, 1.0, 0.7);   // moss on the crown
  const hz = 7.6 + (s.moving ? Math.sin(ph) * 0.3 : 0);
  b.rod([0, by - 1.2, 5.2], [0, by - 0.6, hz - 1.2], 1.6, skinD);                 // the neck
  b.ball(0, by - 0.4, hz, 2.0, skin, 1.9, 1.6);                                   // the head
  b.dot(-0.9, by + 0.3, hz + 1.2, '#241a24'); b.dot(0.9, by + 0.3, hz + 1.2, '#241a24');
  if (s.stun > 0) stars(b0, t, (by + 7) * K);
  return b.ops();
}
/* THE BRAMBLE MOTHER AS A TANGLE (the character sheet): no ball, but vines -- a dozen woody canes arching out of a
 * knot and down to the ground as legs, leafy clumps along them, thorns, and red berries hung in the tangle. */
function brambleMotherMesh(s: CreatureState, C: number, F: number): Op[] {
  const t = s.anim; const K = 2.5; const b0 = stage(s.dir, C, F); const b = magnify(b0, K);
  const cane = '#5e5a34', cane2 = '#4a4a2a', leaf = '#6f8a44', leaf2 = '#88a052', berry = '#c8402a', thorn = '#2e2a1c';
  const hot = s.alight > 0, sway = Math.sin(t * 0.12) * 0.4, ph = s.moving ? t * 0.35 : 0, by = 3.6 - (s.reel > 0 ? 0.5 : 0);   // squat: a thicket hugs the ground
  b.slab(0, 0.5, 5.6, '#00000026');
  // canes arching out of the thicket low over the ground in every direction: her legs and her reach
  for (let i = 0; i < 10; i++) { const a = (i / 10) * Math.PI * 2 + 0.15 * (i % 3), lift = Math.max(0, Math.sin(ph + i * 1.3)) * 1.0;
    const mid: XYZ = [Math.cos(a) * 6.0 + sway * 0.5, by + 0.9, Math.sin(a) * 5.0], end: XYZ = [Math.cos(a) * 8.8, lift, Math.sin(a) * 7.2];
    const col = hot && (t + i) % 3 === 0 ? '#e0703a' : i % 2 ? cane : cane2;
    b.rod([Math.cos(a) * 2.6, by - 0.4, Math.sin(a) * 2.2], mid, 0.85, col); b.rod(mid, end, 0.7, col);
    b.dot(mid[0], mid[1] + 0.5, mid[2], thorn); b.dot((mid[0] + end[0]) / 2, (mid[1] + end[1]) / 2 + 0.4, (mid[2] + end[2]) / 2, thorn); }
  // the thicket: a dome of leafy clumps, broken at its edge
  for (let i = 0; i < 16; i++) { const a = i * 2.39996, r = 1.4 + 4.0 * Math.sqrt((i + 0.5) / 16), h = by + 3.8 - r * 0.55 + Math.sin(i * 1.9) * 0.5;
    b.ball(Math.cos(a) * r + sway * (h - by) * 0.15, h, Math.sin(a) * r * 0.85, 1.9 - r * 0.12, i % 3 === 0 ? leaf2 : i % 3 === 1 ? leaf : '#5f7a3c', 1.6, 1.4); }
  // berries hung in the tangle and thorns on its skin
  for (let i = 0; i < 7; i++) { const a = i * 0.9 + 0.5, r = 2 + (i % 3) * 1.1; b.ball(Math.cos(a) * r, by + 3.4 - r * 0.45, Math.sin(a) * r * 0.85, 0.75, hot ? '#f0a040' : berry, 0.75, 0.75); }
  for (let i = 0; i < 9; i++) { const a = i * 0.7 + 0.2, r = 3.6 + (i % 2) * 0.8; b.dot(Math.cos(a) * r, by + 2.8 - r * 0.4, Math.sin(a) * r * 0.85, thorn); }
  if (s.stun > 0) stars(b0, t, (by + 8) * K);
  return b.ops();
}

const PLANS: Record<string, (s: CreatureState, C: number, F: number) => Op[]> = { sporeling, bramble, wisp, shellback, charger, sow, oldshell, bramblemother, gloam, cinder, eel };
/** Plans the 3D view builds bodies from while the 2D view keeps the authored pixel sprite (LS-416): reached by `creatureOps`, not `hasRig`. */
const MESH_PLANS: Record<string, (s: CreatureState, C: number, F: number) => Op[]> = { tidecrab, mossback, sunbasker, screehopper, stormgull, duskling };
/** A beast whose 3D body is its own plan while its 2D sprite keeps the scaled one (the sow, 2026-09-26). */
const MESH_ONLY: Record<string, (s: CreatureState, C: number, F: number) => Op[]> = { sow: sowMesh, oldshell: oldshellMesh, bramblemother: brambleMotherMesh };
/** The drawing ops of a creature in a state, for the checks (the art lens, round 47). */
export function planOps(s: CreatureState, C = 64, F = 0): Op[] { return (PLANS[s.type] || MESH_PLANS[s.type])(s, C, F); }
const INKS: Record<string, string> = { wisp: P.K };

/** The colours a body of this kind is made of: what its chunks are painted in. */
export function coloursOf(type: string, alt: string | null = null, enraged = false): string[] {
  const a = alt ? { ...P, ...ALT[enraged && ALT[alt + 'r'] ? alt + 'r' : alt] } : P;
  switch (type) {
    case 'sporeling': return [P.p, P.P, P.u];
    case 'bramble': return [P.G, P.g, P.d, P.r];
    case 'wisp': return [P.t, P.w, P.K];
    case 'shellback': return [P.N, P.n, P.b, P.u];
    case 'charger': return [P.C, P.b, P.e];
    case 'gloam': return [P.m, P.M, P.d];
    case 'cinder': return [P.d, P.r, P.y];
    case 'eel': return [P.b, P.C, P.s];
    case 'sow': return ['#4a2e26', '#2f1c18', '#e6dcc4'];
    case 'oldshell': return ['#3e4a3a', '#2a3328', '#d8d0b8'];
    case 'bramblemother': return ['#2e4a22', '#1d3016', '#c8402a'];
    default: return [UI.linen, UI.bone];
  }
}

/** Squash a painted body about its floor line: wider by `k`, shorter by `k`,
 *  the way a thing that just came down looks for a few frames. */
function squashOps(ops: Op[], C: number, F: number, k: number): Op[] {
  const sx = (x: number) => C + (x - C) * (1 + k), sy = (y: number) => F - (F - y) * (1 - k);
  return ops.map((op) => op[0] === 'cap' ? ['cap', [sx(op[1][0]), sy(op[1][1])], [sx(op[2][0]), sy(op[2][1])], op[3], op[4]] as Op
    : op[0] === 'disc' ? ['disc', sx(op[1]), sy(op[2]), op[3] * (1 + k), op[4] * (1 - k), op[5]] as Op
    : ['px', sx(op[1]), sy(op[2]), op[3]] as Op);
}

/* ---------------------------------------------------------------- cache --- */

/* WHERE THE SPRITE SITS IN THE ATLAS IS PART OF THE ANSWER, and its key with it. Every entry built below is
 * `{ ...bakeGrid(...), w, h, key }`, and `bakeGrid` returns the atlas cell -- `canvas`, `sx`, `sy`, `mask`. The
 * declared shape named four of those six, so five reads of `.sx`, `.sy` and `.key` in `entities.ts` were errors
 * against a type that was simply wrong. The same fault, in the same week, as `rig.ts`'s pose cache (2026-09-06). */
const cache = new Map<string, { canvas: any; sx: number; sy: number; mask: Uint8Array; w: number; h: number; key: string }>();
onAtlasReset(() => cache.clear());

/** True if this kind has a body plan. */
export const hasRig = (type: string) => !!PLANS[type];

/** The creature painted for its state: a canvas the size of its cell, centred
 *  on the body's centre. */
export function creatureSprite(s: CreatureState) {
  const plan = PLANS[s.type];
  if (!plan) return null;
  const size = CELL[s.type] || 24;
  const C = size / 2, F = size / 2 + 7;
  let ops = plan(s, C, F);
  /* A BODY MOVES WHEN IT WALKS, AND BREATHES WHEN IT STANDS (LS-234): the
   * limbs were animated and the bodies were not -- seven to eleven percent of
   * a walking creature's pixels changed between frames, and a standing
   * bramble was a still picture. Every opaque part above the floor line rises
   * and falls with the stride, and a standing body breathes slowly; the
   * ground shadow (translucent) stays where it is. */
  { const bob = s.moving || s.state === 'charge' ? Math.abs(Math.sin(s.anim * 0.3)) * 0.9 : (Math.sin(s.anim * 0.07) * 0.35 + 0.35);
    ops = ops.map((op) => op[0] === 'cap' ? ['cap', [op[1][0], op[1][1] - bob], [op[2][0], op[2][1] - bob], op[3], op[4]] as Op
      : op[0] === 'disc' ? (op[5].length > 7 ? op : ['disc', op[1], op[2] - bob, op[3], op[4], op[5]] as Op)
      : ['px', op[1], op[2] - bob, op[3]] as Op); }
  if (s.landed && s.landed > 0) ops = squashOps(ops, C, F, Math.min(1, s.landed / 8) * 0.3);
  if (s.wet) ops = ops.map((op) => op[0] === 'cap' ? ['cap', op[1], op[2], op[3], wetTone(op[4], 0.85)] as Op : op[0] === 'disc' ? ['disc', op[1], op[2], op[3], op[4], wetTone(op[5], 0.85)] as Op : ['px', op[1], op[2], wetTone(op[3], 0.85)] as Op);   // its wet state: the same body, darker and cooler, its lit tones kept (2026-09-11)
  const key = keyOf(s.type, ops, s.white, !!s.wet);
  let hit = cache.get(key);
  if (hit) return hit;
  const gr = paint(ops, size, size, INKS[s.type] || P.o);
  const baked = bakeGrid(gr, s.white);
  hit = { ...baked, w: size, h: size, key };
  if (cache.size > 4000) cache.clear();   // cells of a shared atlas (LS-235), not canvases
  cache.set(key, hit);
  return hit;
}

export function creatureOps(s: CreatureState) {
  const plan = ((globalThis as any).__m3Capture && MESH_ONLY[s.type]) || PLANS[s.type] || MESH_PLANS[s.type];
  if (!plan) return null;
  const size = CELL[s.type] || 24;
  return plan(s, size / 2, size / 2 + 7);
}
